import React from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Star } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useSettings } from '../hooks/useSettings';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { settings } = useSettings();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contactos" className="bg-[#071028] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="space-y-6">
            <img
              src="/logo-marcelo.png"
              alt="Marcelo Carvalho Transportes"
              className="h-20 w-auto"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              Mudanças e transportes profissionais em Lisboa e região.
              Segurança, pontualidade e confiança em cada serviço.
            </p>
            {/* Social Media */}
            <div className="flex gap-4">
              <a
                href={settings.facebook || "https://www.facebook.com/profile.php?id=61584444300983"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#1FA2FF] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={settings.instagram || "https://www.instagram.com/marcelocarvalho.transportes"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#1FA2FF] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://share.google/JqU1dPRsi28bsfNRN"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#1FA2FF] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Google Reviews"
              >
                <Star className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-[#1FA2FF]">Links Rápidos</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('servicos')}
                  className="text-gray-300 hover:text-[#1FA2FF] transition-colors text-sm"
                >
                  Serviços
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('como-funciona')}
                  className="text-gray-300 hover:text-[#1FA2FF] transition-colors text-sm"
                >
                  Como Funciona
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testemunhos')}
                  className="text-gray-300 hover:text-[#1FA2FF] transition-colors text-sm"
                >
                  Testemunhos
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="text-gray-300 hover:text-[#1FA2FF] transition-colors text-sm"
                >
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* Services & Areas */}
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-bold mb-6 text-[#1FA2FF]">Nossos Serviços</h3>
              <ul className="space-y-3 text-sm text-gray-300">
                <li>Mudanças Domésticas</li>
                <li>Mudanças Comerciais</li>
                <li>Transporte de Móveis</li>
                <li>Montagem e Desmontagem</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-[#1FA2FF]">Áreas de Atuação</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Lisboa (Centro)</li>
                <li>Cascais e Estoril</li>
                <li>Sintra e Queluz</li>
                <li>Loures e Odivelas</li>
                <li>Margem Sul (Almada/Seixal)</li>
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-[#1FA2FF]">Contactos</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#1FA2FF] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">{companyInfo.location}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#1FA2FF] flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${settings.whatsappPhone}`}
                  className="text-sm text-gray-300 hover:text-[#1FA2FF] transition-colors"
                >
                  {settings.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#1FA2FF] flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${settings.email}`}
                  className="text-sm text-gray-300 hover:text-[#1FA2FF] transition-colors break-words"
                >
                  {settings.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#1FA2FF] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">{companyInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>
              © {new Date().getFullYear()} Marcelo Carvalho Transportes. Todos os direitos reservados.
            </p>
            <div className="flex gap-6">
              <Link to="/" className="hover:text-[#1FA2FF] transition-colors">
                Início
              </Link>
              <Link to="/privacidade" className="hover:text-[#1FA2FF] transition-colors">
                Política de Privacidade
              </Link>
              <Link to="/termos" className="hover:text-[#1FA2FF] transition-colors">
                Termos e Condições
              </Link>
            </div>

          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
